/**
 * useScope — resolve o escopo de visualizacao de chamados (own/org/global).
 *
 * Spec: docs/CHAMADOS_VISIBILIDADE_SPEC.md §9.2.1
 *
 * Regras:
 *  - maxAllowedScope: derivado da UNIAO de user.global_permissions + todas
 *    user.tenant_permissions (admin:full ou chamados:view_global -> 'global';
 *    chamados:view_org -> 'org'; senao 'own').
 *  - Modo preview: respeita previewTemplate.permissions (NAO bypass).
 *  - allowedScopes: ['own'] sempre; +'org' se max>=org OU se membro de org
 *    com >=2 tenants; +'global' se max=='global'.
 *  - G-N10: durante isResolving (user nulo OU orgs carregando), retorna
 *    allowedScopes=['own'] para evitar UI flicker.
 *  - scope efetivo: URL ?scope=... > localStorage('chamados.lastScope') > default.
 *  - setScope persiste em localStorage e (por padrao) sincroniza URL.
 */
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuthStore } from '@/store/auth'
import { useUserOrganizations } from '@/hooks/useUserOrganizations'

export type ChamadoScope = 'own' | 'org' | 'global'

const SCOPE_LS_KEY = 'chamados.lastScope'

export interface UseScopeReturn {
  scope: ChamadoScope
  setScope: (s: ChamadoScope, options?: { syncUrl?: boolean }) => void
  allowedScopes: ChamadoScope[]
  defaultScope: ChamadoScope
  maxAllowedScope: ChamadoScope
  isResolving: boolean
}

export function useScope(): UseScopeReturn {
  const { user, previewTemplate } = useAuthStore()
  const orgsQuery = useUserOrganizations()
  // useUserOrganizations (TASK-2467) retorna o useQuery result cru. Adapter:
  const orgs = orgsQuery.data ?? []
  const orgsLoading = orgsQuery.isLoading
  const [searchParams, setSearchParams] = useSearchParams()

  const isResolving = !user || orgsLoading

  // ── Determinar maxAllowedScope a partir de permissions (NAO 'role') ──────
  const maxAllowedScope: ChamadoScope = useMemo(() => {
    if (!user) return 'own'

    // Modo preview: respeita o template (NAO bypass).
    if (previewTemplate) {
      const perms = previewTemplate.permissions ?? []
      if (perms.includes('admin:full') || perms.includes('chamados:view_global')) return 'global'
      if (perms.includes('chamados:view_org')) return 'org'
      return 'own'
    }

    const globals = user.global_permissions ?? []
    const allTenantPerms = Object.values(user.tenant_permissions ?? {}).flat()
    const allPerms = new Set<string>([...globals, ...allTenantPerms])

    if (allPerms.has('admin:full') || allPerms.has('chamados:view_global')) return 'global'
    if (allPerms.has('chamados:view_org')) return 'org'
    return 'own'
  }, [user, previewTemplate])

  // ── allowedScopes ─────────────────────────────────────────────────────────
  const allowedScopes: ChamadoScope[] = useMemo(() => {
    // G-N10: durante resolucao, retornar somente 'own' para evitar flicker.
    if (isResolving) return ['own']

    const result: ChamadoScope[] = ['own']
    const orgFromMax = maxAllowedScope === 'org' || maxAllowedScope === 'global'
    const orgFromMembership = orgs.some((o) => o.tenant_count >= 2)
    if (orgFromMax || orgFromMembership) result.push('org')
    if (maxAllowedScope === 'global') result.push('global')
    return result
  }, [maxAllowedScope, orgs, isResolving])

  const defaultScope: ChamadoScope = useMemo(() => maxAllowedScope, [maxAllowedScope])

  // ── Resolucao do scope efetivo: URL > localStorage > default ─────────────
  const urlScope = searchParams.get('scope') as ChamadoScope | null
  const lsScope =
    typeof window !== 'undefined'
      ? (localStorage.getItem(SCOPE_LS_KEY) as ChamadoScope | null)
      : null

  const resolvedScope: ChamadoScope = useMemo(() => {
    const requested: ChamadoScope = (urlScope ?? lsScope ?? defaultScope) as ChamadoScope
    return allowedScopes.includes(requested) ? requested : defaultScope
  }, [urlScope, lsScope, defaultScope, allowedScopes])

  const [scope, setScopeState] = useState<ChamadoScope>(resolvedScope)
  useEffect(() => {
    setScopeState(resolvedScope)
  }, [resolvedScope])

  // Sem o seletor na página (spec 2026-09-27-chamados-revisao-geral §7), quem escolhe o
  // escopo é o menu, pela URL. Lembrar a escolha aqui mantém `/chamados` sem query (o
  // "cancelar" e o "criado" do NovoChamado) no último escopo escolhido no menu; sem isto
  // o `localStorage` congelaria na última escolha feita no seletor que saiu.
  useEffect(() => {
    if (isResolving || !urlScope || !allowedScopes.includes(urlScope)) return
    try {
      localStorage.setItem(SCOPE_LS_KEY, urlScope)
    } catch {
      // ignora QuotaExceededError em modo privado / disabled storage
    }
  }, [urlScope, allowedScopes, isResolving])

  // O menu abre `/chamados` sem query (spec 2026-10-21-chamados-menu-todos-os-chamados,
  // decisão 3): o escopo resolvido vai para a URL, com replace, porque o seletor de tenant
  // da sidebar decide pelo `?scope` (TASK-2473). Sai do `resolvedScope`, e não do `scope`
  // (estado, um render atrasado): gravar o `own` provisório prenderia o admin nele, já que
  // a URL vence. Único chamador do hook: `ChamadosInner`.
  useEffect(() => {
    if (isResolving || urlScope) return
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set('scope', resolvedScope)
        return next
      },
      { replace: true },
    )
  }, [isResolving, urlScope, resolvedScope, setSearchParams])

  const setScope = (s: ChamadoScope, options?: { syncUrl?: boolean }) => {
    setScopeState(s)
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(SCOPE_LS_KEY, s)
      } catch {
        // ignora QuotaExceededError em modo privado / disabled storage
      }
    }
    if (options?.syncUrl !== false) {
      const next = new URLSearchParams(searchParams)
      next.set('scope', s)
      setSearchParams(next, { replace: false })
    }
  }

  return { scope, setScope, allowedScopes, defaultScope, maxAllowedScope, isResolving }
}
